/**
 * CR-7261: carry the clicked chart figure into the drill-down header.
 *
 * Hover January on the OTD chart and the tooltip says 91.95%; drill in and
 * that number used to vanish — the header carried a context LABEL ("January
 * 2026") and never a VALUE, so the headline could not be reconciled against
 * the records beneath it.
 *
 * The number is formatted here with the same rule the chart tooltip uses, so
 * the header and the tooltip can never disagree about rounding: a header
 * reading 92.0% next to a tooltip reading 91.95% would be a worse bug than
 * the one being fixed.
 */
import type { DrilldownHeadline } from '../../contexts/DrilldownContext';

/** The one percent format the dashboard's chart tooltips use. */
export function formatChartPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

/** Headline label shown before the figure in the drill-down header. */
export const CHART_HEADLINE_LABEL = 'chart';

/**
 * The OTD headline for a clicked month, from the point's own values.
 *
 * @param point - the Recharts payload of the clicked month (`otd`, optional `target`)
 * @returns the headline, or undefined when the point carries no OTD figure
 */
export function otdHeadlineFromPoint(point: Record<string, unknown> | null | undefined): DrilldownHeadline | undefined {
  if (!point || point['otd'] == null || point['otd'] === '') return undefined;
  const otd = Number(point['otd']);
  if (!Number.isFinite(otd)) return undefined;
  const hasTarget = point['target'] != null && point['target'] !== '';
  const target = hasTarget ? Number(point['target']) : NaN;
  const value = Number.isFinite(target)
    ? `${formatChartPercent(otd)} (target ${formatChartPercent(target)})`
    : formatChartPercent(otd);
  return { label: CHART_HEADLINE_LABEL, value };
}
